
export class LanguageDetectionService {
  private static readonly API_URL = 'https://ipapi.co/json/';
  private static readonly STORAGE_KEY = 'psyonline_language';
  private static readonly COUNTRY_TO_LANGUAGE = {
    // Português Brasil
    'BR': 'pt-BR',
    
    // Português Portugal  
    'PT': 'pt-PT', 'AO': 'pt-PT', 'MZ': 'pt-PT', 'CV': 'pt-PT',
    'GW': 'pt-PT', 'ST': 'pt-PT', 'TL': 'pt-PT',
    
    // Inglês
    'US': 'en', 'GB': 'en', 'CA': 'en', 'AU': 'en', 'NZ': 'en',
    'IE': 'en', 'ZA': 'en', 'SG': 'en', 'MY': 'en', 'PH': 'en',
    
    // Espanhol
    'ES': 'es', 'MX': 'es', 'AR': 'es', 'CO': 'es', 'PE': 'es',
    'VE': 'es', 'CL': 'es', 'EC': 'es', 'GT': 'es', 'CU': 'es',
    'BO': 'es', 'DO': 'es', 'HN': 'es', 'PY': 'es', 'SV': 'es',
    'NI': 'es', 'CR': 'es', 'PA': 'es', 'UY': 'es',
    
    // Russo
    'RU': 'ru', 'BY': 'ru', 'KZ': 'ru', 'KG': 'ru', 'TJ': 'ru',
    'UZ': 'ru', 'AM': 'ru', 'AZ': 'ru', 'GE': 'ru', 'MD': 'ru',
    
    // Alemão
    'DE': 'de', 'AT': 'de', 'CH': 'de', 'LI': 'de', 'LU': 'de',
    
    // Mandarim (Chinês)
    'CN': 'zh-cn', 'TW': 'zh-cn', 'HK': 'zh-cn', 'MO': 'zh-cn',
    
    // Hindi
    'IN': 'hi', 'NP': 'hi', 'FJ': 'hi',
    
    // Árabe
    'SA': 'ar', 'AE': 'ar', 'EG': 'ar', 'JO': 'ar', 'LB': 'ar',
    'SY': 'ar', 'IQ': 'ar', 'KW': 'ar', 'QA': 'ar', 'BH': 'ar',
    'OM': 'ar', 'YE': 'ar', 'MA': 'ar', 'TN': 'ar', 'DZ': 'ar',
    'LY': 'ar', 'SD': 'ar', 'SO': 'ar', 'DJ': 'ar', 'KM': 'ar',
    
    // Francês
    'FR': 'fr', 'BE': 'fr', 'MC': 'fr', 'SN': 'fr', 'CI': 'fr',
    'ML': 'fr', 'BF': 'fr', 'NE': 'fr', 'TD': 'fr', 'MG': 'fr',
    'CM': 'fr', 'CG': 'fr', 'GA': 'fr', 'CF': 'fr', 'VU': 'fr',
    'NC': 'fr', 'PF': 'fr', 'WF': 'fr', 'RE': 'fr', 'GP': 'fr',
    'MQ': 'fr', 'GF': 'fr', 'YT': 'fr',
    
    // Italiano
    'IT': 'it', 'SM': 'it', 'VA': 'it', 'MT': 'it'
  } as Record<string, string>;

  static async detectLanguage(): Promise<string> {
    const savedLanguage = localStorage.getItem(this.STORAGE_KEY);
    if (savedLanguage) return savedLanguage;

    try {
      const response = await fetch(this.API_URL);
      const data = await response.json();
      const countryCode = data.country_code;
      
      const detectedLanguage = this.COUNTRY_TO_LANGUAGE[countryCode] || 'pt-BR';
      localStorage.setItem(this.STORAGE_KEY, detectedLanguage);
      
      return detectedLanguage;
    } catch (error) {
      const browserLanguage = navigator.language;
      if (browserLanguage.startsWith('pt-PT')) return 'pt-PT';
      if (browserLanguage.startsWith('pt')) return 'pt-BR';
      if (browserLanguage.startsWith('en')) return 'en';
      if (browserLanguage.startsWith('es')) return 'es';
      if (browserLanguage.startsWith('ru')) return 'ru';
      if (browserLanguage.startsWith('de')) return 'de';
      if (browserLanguage.startsWith('zh')) return 'zh-cn';
      if (browserLanguage.startsWith('hi')) return 'hi';
      if (browserLanguage.startsWith('ar')) return 'ar';
      if (browserLanguage.startsWith('fr')) return 'fr';
      if (browserLanguage.startsWith('it')) return 'it';
      
      return 'pt-BR';
    }
  }

  static saveLanguagePreference(language: string): void {
    localStorage.setItem(this.STORAGE_KEY, language);
  }

  static getAvailableLanguages() {
    return [
      { code: 'pt-BR', name: 'Português (Brasil)', flag: '🇧🇷', nativeName: 'Português' },
      { code: 'pt-PT', name: 'Português (Portugal)', flag: '🇵🇹', nativeName: 'Português' },
      { code: 'en', name: 'English', flag: '🇺🇸', nativeName: 'English' },
      { code: 'es', name: 'Español', flag: '🇪🇸', nativeName: 'Español' },
      { code: 'ru', name: 'Русский', flag: '🇷🇺', nativeName: 'Русский' },
      { code: 'de', name: 'Deutsch', flag: '🇩🇪', nativeName: 'Deutsch' },
      { code: 'zh-cn', name: '中文 (简体)', flag: '🇨🇳', nativeName: '中文' },
      { code: 'hi', name: 'हिन्दी', flag: '🇮🇳', nativeName: 'हिन्दी' },
      { code: 'ar', name: 'العربية', flag: '🇸🇦', nativeName: 'العربية', rtl: true },
      { code: 'fr', name: 'Français', flag: '🇫🇷', nativeName: 'Français' },
      { code: 'it', name: 'Italiano', flag: '🇮🇹', nativeName: 'Italiano' }
    ];
  }
}
